import { type ActionEvent, Controller } from "@hotwired/stimulus"
import Supercluster, { type AnyProps, type ClusterFeature, type PointFeature } from "supercluster"

import LoaderController from "../loader_controller"

import { pluralize } from "@/helpers/pluralize"
import {
  type AdvancedMarkerElement,
  type MapPin,
  ACTIVE_CLASS,
  HIGHLIGHTED_CLASS,
  Z_INDEX,
  cardContent,
  clusterMarkerContent,
  markerKey,
  pinMarkerContent,
  pointsFromPins,
  setSearchBarHeightForCSS,
  toggleMarkerState,
  urlWithBounds
} from "@/helpers/search_map"
import { sessionStorage } from "@/helpers/storage"
import { renderTurboStream } from "@/helpers/turbo"
import { debounce } from "@/helpers/utils"

const NEW_SEARCH_PAGE = document.body.getAttribute("data-new-search-page") === "true"

const DESKTOP_BREAKPOINT = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue("--desktop-map-breakpoint")
const MAP_OPEN_CLASS = "--map-open"
const RESULTS_COLLAPSED_CLASS = "--results-collapsed"
const SESSION_STORAGE_KEYS = Object.freeze({
  MAP_OPEN: "search.map.map_open", // TODO[NEW_SEARCH_PAGE]: Remove this, only results key needed.
  RESULTS_OPEN: "search.map.results_open"
} as const)

export default class SearchMapController extends Controller {
  static outlets = ["loader"]
  declare readonly loaderOutlet: LoaderController

  static targets = ["map", "mapPins", "markerContent"]
  declare readonly mapTarget: HTMLElement
  declare readonly markerContentTarget: HTMLElement

  static values = {
    mapCenter: Object,
    mapOpen: { type: Boolean, default: false },
    resultsOpen: { type: Boolean, default: false }
  }
  declare mapCenterValue: { lat: number; lng: number }
  declare mapOpenValue: boolean
  declare resultsOpenValue: boolean

  declare private static LatLngBounds: typeof google.maps.LatLngBounds
  declare private static MarkerLibrary: google.maps.MarkerLibrary

  private activeMarker: AdvancedMarkerElement | null = null
  declare private googleMap: google.maps.Map
  private isMapPanned = window.mapData.isMapPanned
  private lookupAbort = new window.AbortController()
  private markers: Map<string, AdvancedMarkerElement> = new Map()
  declare private supercluster: Supercluster

  initialize(): void {
    this.onViewportChange = debounce(this.onViewportChange.bind(this), 250)

    if (NEW_SEARCH_PAGE) {
      this.openResults(true)
    } else if (sessionStorage) {
      this.mapOpenValue = sessionStorage.getItem(SESSION_STORAGE_KEYS.MAP_OPEN) !== null
      this.resultsOpenValue = sessionStorage.getItem(SESSION_STORAGE_KEYS.RESULTS_OPEN) !== null
    }
  }

  mapPinsTargetConnected(): void {
    this.isMapPanned = window.mapData.isMapPanned

    this.refreshMarkers()
  }

  async mapOpenValueChanged(): Promise<void> {
    if (this.mapOpenValue) {
      if (!this.googleMap) {
        await this.initGoogleMap()
      }

      window.scrollTo(0, 0)
      setSearchBarHeightForCSS()
      document.body.classList.add(MAP_OPEN_CLASS)

      if (!NEW_SEARCH_PAGE) {
        sessionStorage?.setItem(SESSION_STORAGE_KEYS.MAP_OPEN, "")
      }

      this.mapTarget.hidden = false
    } else {
      this.deactivateMarker()
      document.body.classList.remove(MAP_OPEN_CLASS, RESULTS_COLLAPSED_CLASS)

      if (!NEW_SEARCH_PAGE) {
        sessionStorage?.removeItem(SESSION_STORAGE_KEYS.MAP_OPEN)
      }

      this.mapTarget.hidden = true
    }

    this.replaceHistoryWithMapState()
    this.loaderOutlet.stop()
  }

  resultsOpenValueChanged(): void {
    // Toggle class on body instead of relevant element to prevent it getting removed by
    // Turbo stream response.
    if (NEW_SEARCH_PAGE) {
      document.body.classList.toggle(RESULTS_COLLAPSED_CLASS, !this.resultsOpenValue)
    } else {
      document.body.classList.toggle(RESULTS_COLLAPSED_CLASS, this.resultsOpenValue)
    }

    if (this.resultsOpenValue) {
      sessionStorage?.setItem(SESSION_STORAGE_KEYS.RESULTS_OPEN, "")
    } else {
      sessionStorage?.removeItem(SESSION_STORAGE_KEYS.RESULTS_OPEN)
    }
  }

  deactivateMarker(): void {
    if (this.activeMarker) {
      toggleMarkerState(this.activeMarker, ACTIVE_CLASS, false)

      this.markerContentTarget.textContent = ""
      this.activeMarker = null
    }
  }

  hide(): void {
    this.mapOpenValue = false
    this.resultsOpenValue = false
    this.pushHistoryWithMapState()
  }

  get mapBounds(): google.maps.LatLngBounds | undefined {
    if (NEW_SEARCH_PAGE) {
      // This function called when applying filters, which can obviously happen before map is initialized.
      return this.googleMap?.getBounds()
    }
  }

  onPopState({ state }: PopStateEvent): void {
    // This function runs for any `popstate` on the page, not just map-related.
    if (state?.mapOpen === undefined) {
      return
    }

    if (state.mapOpen !== this.mapOpenValue) {
      this.mapOpenValue = !this.mapOpenValue
    }
  }

  onViewportChange(): void {
    setSearchBarHeightForCSS()

    if (NEW_SEARCH_PAGE && sessionStorage) {
      this.openResults(sessionStorage.getItem(SESSION_STORAGE_KEYS.RESULTS_OPEN) !== null)
    }
  }

  refreshMarkers(): void {
    // This function is also called on `#turbo-filters-*` frame loads, which can obviously happen
    // before the map is ever initialized.
    if (!this.googleMap) {
      return
    }

    // Refresh supercluster and update markers.
    this.supercluster.load(pointsFromPins())
    this.updateMarkers()

    // Fit bounds to center the map on the new markers, but not if user panned map.
    if (this.markers.size && !this.isMapPanned) {
      // The `fitBounds` method automatically adjusts zoom level which may force clusters
      // to expand or collapse, so update markers again after next idle.
      window.google.maps.event.addListenerOnce(this.googleMap, "idle", () => {
        this.updateMarkers()
      })

      this.googleMap.fitBounds(this.boundsForAllPoints)
    }
  }

  replaceHistoryWithMapState(): void {
    if (!NEW_SEARCH_PAGE) {
      window.history.replaceState({ ...window.history.state, mapOpen: this.mapOpenValue }, "")
    }
  }

  show(): void {
    this.mapOpenValue = true
    this.pushHistoryWithMapState()
  }

  toggleHighlightMarker({ params: { venueId } }: ActionEvent): void {
    const marker = this.markers.get(`pin:${venueId}`)

    if (marker) {
      toggleMarkerState(marker, HIGHLIGHTED_CLASS)
    }
  }

  toggleResultsList(): void {
    if (NEW_SEARCH_PAGE) {
      this.openResults(!this.resultsOpenValue)
    } else {
      this.resultsOpenValue = !this.resultsOpenValue
    }
  }

  private activateMarker(marker: AdvancedMarkerElement, pin: MapPin): void {
    // Deactivate any current active marker.
    this.deactivateMarker()

    this.markerContentTarget.appendChild(cardContent(pin))
    toggleMarkerState(marker, ACTIVE_CLASS, true)

    this.activeMarker = marker
  }

  private appendHideControl(): void {
    const button = document.createElement("button")
    button.className = "ui-component-button variant:input search__map__hide-map"
    button.innerHTML = `<i class="far fa-xmark fa:before" aria-hidden="true"></i> Hide map`
    button.type = "button"

    button.addEventListener("click", this.hide.bind(this))
    this.googleMap.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(button)
  }

  private appendCustomControls(): void {
    const sharedClasses =
      "bg-white rounded-8 shadow-[0_1px_4px_-1px_color-mix(in_srgb,var(--color-black),70%_transparent)] text-base"

    const toggleButton = document.createElement("button")
    toggleButton.type = "button"
    toggleButton.className = `${sharedClasses} hidden lg:flex items-center aspect-square px-10 py-10 mt-10 ml-10`
    toggleButton.innerHTML = `
      <span class="sr-only">Toggle results</span>
      <i class="fas fa-chevron-left fa-fw [.--results-collapsed_&]:rotate-180" aria-hidden="true"></i>
    `
    toggleButton.addEventListener("click", this.toggleResultsList.bind(this))
    this.googleMap.controls[window.google.maps.ControlPosition.TOP_LEFT].push(toggleButton)

    const zoomInButton = document.createElement("button")
    zoomInButton.className = "py-10 border-secondary"
    zoomInButton.innerHTML = `
      <span class="sr-only">Zoom in</span>
      <i class="fas fa-plus fa-fw aria-hidden="true"></i>
    `
    zoomInButton.type = "button"
    zoomInButton.addEventListener("click", () => {
      this.googleMap.setZoom(this.googleMap.getZoom()! + 1)
    })

    const zoomOutButton = document.createElement("button")
    zoomOutButton.className = "py-10"
    zoomOutButton.innerHTML = `
      <span class="sr-only">Zoom out</span>
      <i class="fas fa-minus fa-fw aria-hidden="true"></i>
    `
    zoomOutButton.type = "button"
    zoomOutButton.addEventListener("click", () => {
      this.googleMap.setZoom(this.googleMap.getZoom()! - 1)
    })

    const zoomContainer = document.createElement("div")
    zoomContainer.className = `${sharedClasses} flex flex-col divide-y px-10 mt-10 mr-10`
    zoomContainer.appendChild(zoomInButton)
    zoomContainer.appendChild(zoomOutButton)
    this.googleMap.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(zoomContainer)
  }

  private get boundsForAllPoints(): google.maps.LatLngBounds {
    const worldBounds = SearchMapController.LatLngBounds.MAX_BOUNDS
    const allPoints = this.pointsInBounds(worldBounds, this.googleMap.get("maxZoom"))
    const bounds = new SearchMapController.LatLngBounds()

    for (const point of allPoints) {
      const [lng, lat] = point.geometry.coordinates
      bounds.extend({ lng, lat })
    }

    return bounds
  }

  private createClusterMarker(key: string, cluster: ClusterFeature<AnyProps>): void {
    if (this.markers.has(key)) {
      return
    }

    const [lng, lat] = cluster.geometry.coordinates
    const count = cluster.properties.point_count
    const marker = new SearchMapController.MarkerLibrary.AdvancedMarkerElement({
      content: clusterMarkerContent(count),
      map: this.googleMap,
      position: { lat, lng },
      title: `${pluralize(count, "spa")} in this area`,
      zIndex: Z_INDEX.CLUSTER
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      const expandedZoomLevel = this.supercluster.getClusterExpansionZoom(+cluster.id!)
      this.googleMap.setZoom(expandedZoomLevel)
      this.googleMap.panTo(marker.position!)
    })

    this.markers.set(key, marker)
  }

  private createPinMarker(key: string, pin: MapPin): void {
    if (this.markers.has(key)) {
      return
    }

    const marker = new SearchMapController.MarkerLibrary.AdvancedMarkerElement({
      content: pinMarkerContent(pin),
      map: this.googleMap,
      position: pin.position,
      title: pin.name,
      zIndex: Z_INDEX.PIN
    }) as AdvancedMarkerElement

    marker.addListener("click", () => {
      if (marker.content.classList.contains(ACTIVE_CLASS)) {
        return
      }

      // Activate clicked marker if it was previously deactivated
      this.activateMarker(marker, pin)
    })

    this.markers.set(key, marker)
  }

  private async fetchMarkers(): Promise<void> {
    if (this.isMapPanned) {
      const url = urlWithBounds(window.location.href, this.googleMap.getBounds())

      // Abort any previous fetch and reset controller.
      this.lookupAbort.abort()
      this.lookupAbort = new window.AbortController()

      try {
        this.loaderOutlet.start()
        await renderTurboStream(url.toString(), { signal: this.lookupAbort.signal })
      } finally {
        this.loaderOutlet.stop()
      }
    } else {
      this.isMapPanned = true
    }
  }

  private async initGoogleMap(): Promise<void> {
    const { importLibrary } = window.google.maps
    const [coreLibrary, mapsLibrary, markerLibrary] = await Promise.all([
      importLibrary("core") as Promise<google.maps.CoreLibrary>,
      importLibrary("maps") as Promise<google.maps.MapsLibrary>,
      importLibrary("marker") as Promise<google.maps.MarkerLibrary>
    ])

    // Set static properties
    SearchMapController.LatLngBounds = coreLibrary.LatLngBounds
    SearchMapController.MarkerLibrary = markerLibrary

    // Initialize map and clusters
    this.googleMap = new mapsLibrary.Map(document.getElementById("js-google-map")!, {
      center: this.mapCenterValue,
      clickableIcons: false,
      disableDefaultUI: true,
      fullscreenControl: false,
      gestureHandling: "greedy", // Move map with one finger instead of two on mobile
      mapId: "dcee0ac2d8a57685", // https://developers.google.com/maps/documentation/javascript/map-ids/mapid-over
      maxZoom: 18,
      minZoom: 6,
      zoom: 6,
      ...(NEW_SEARCH_PAGE ? { zoomControl: false } : {})
    })
    this.supercluster = new Supercluster({ radius: 60 })

    if (NEW_SEARCH_PAGE) {
      this.appendCustomControls()
    } else {
      this.appendHideControl()
    }

    // Wait until first `idle` before initial marker refresh, and add listener for future `idle` events.
    window.google.maps.event.addListenerOnce(this.googleMap, "idle", () => {
      this.refreshMarkers()

      if (NEW_SEARCH_PAGE) {
        // Fetch new markers when idle.
        this.googleMap.addListener("idle", debounce(this.fetchMarkers.bind(this), 100))
      } else {
        // Update markers when idle.
        this.googleMap.addListener("idle", debounce(this.updateMarkers.bind(this), 250))
      }
    })

    // Clicks on map should deactivate open markers.
    this.googleMap.addListener("click", this.deactivateMarker.bind(this))
  }

  private openResults(shouldOpen: boolean): void {
    const isDesktopMap = window.matchMedia(`screen and (min-width: ${DESKTOP_BREAKPOINT})`).matches

    this.resultsOpenValue = shouldOpen
    this.mapOpenValue = isDesktopMap || !shouldOpen
  }

  private pointsInBounds(
    bounds: google.maps.LatLngBounds,
    zoom: number
  ): Array<ClusterFeature<AnyProps> | PointFeature<AnyProps>> {
    const { west, south, east, north } = bounds.toJSON()

    return this.supercluster.getClusters([west, south, east, north], zoom)
  }

  private pushHistoryWithMapState(): void {
    if (!NEW_SEARCH_PAGE) {
      window.history.pushState({ ...window.history.state, mapOpen: this.mapOpenValue }, "")
    }
  }

  private updateMarkers(): void {
    const bounds = this.googleMap.getBounds()
    const zoom = this.googleMap.getZoom()

    if (!bounds || zoom === undefined) {
      return
    }

    // Deactivate any active marker before setting new ones.
    this.deactivateMarker()

    const points = this.pointsInBounds(bounds, zoom)
    const markerKeys = new Set<string>()

    // Check each point and add either pin marker or cluster marker as appropriate.
    for (const point of points) {
      const key = markerKey(point)
      const { pin } = point.properties

      markerKeys.add(key)

      if (pin) {
        this.createPinMarker(key, pin)
      } else {
        this.createClusterMarker(key, point as ClusterFeature<AnyProps>)
      }
    }

    // Delete unused markers, removing click listener and map first.
    for (const [key, marker] of this.markers) {
      if (!markerKeys.has(key)) {
        window.google.maps.event.clearInstanceListeners(marker)
        marker.map = null
        this.markers.delete(key)
      }
    }
  }
}
